import { Labels } from '../../global/labels';
import { UIDateElement } from './ui-dateelement';
import { browser } from '../../global/browser';
import { eventBus } from '../../global/event-bus';
import { Digest } from '../../global/digest';
import { addLeadingZero } from '../../global/helpers';
import {
    detachChildNodes,
    hide,
    insertElements,
    setAttributes,
    show,
    updateElement,
    dispatchNativeEvent,
    getNativeInputDescriptor,
    setNativeInputValue,
} from '../../global/ui-helpers';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type {
    AttributeValue,
    ObservedAttributes,
    ElementConfig,
} from '../ui-element.type';
import type { NullOr, UndefinedOr } from '../../global/utils.type';
import type { TranslationLabels } from '../../types/labels.type';
import type {
    UIMonthPickerModes,
    UIMonthPickerMode,
    UIMonthPickerLayouts,
    UIMonthPickerVisibleMonths,
} from './ui-monthpicker.type';
import styles from './ui-monthpicker.css';
import { validationLabels } from '../form/validation-labels';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIDateElement}
 * @alias UIMonthPicker
 * @element ui-monthpicker
 * @implements {CustomFormComponent}
 * @classdesc Represents a class for <code>ui-monthpicker</code> element.
 * Allows to pick months. Usually used with combination with ui-calendar.
 * @fires event:change
 * @fires event:blur
 * @property {Locale} [locale="en"] {@attr locale} Localization rules.
 * @property {string} [controlId] {@attr control-id} Base id for month/year selects (SELECT mode only).
 * Deprecated for INPUT mode (if component is used on mobile only, but not on desktop) - set id directly on the native input element instead.
 * When in SELECT mode (component is used on desktop too), if control-id is provided, it is used as the base id for month/year selects.
 * Otherwise, the input's id (if set) is used as the base, or a random id is generated automatically.
 * @property {string} [layout] {@attr layout} Layout for month picker.
 * @property {boolean} [allowEmpty] {@attr allow-empty} Makes the value optional, allowing users to clear the selection.
 * @property {string} [valueType="month"] {@attr value-type}
 * @property {HTMLSelectElement} monthSelect Shortcut for native month select.
 * @property {HTMLSelectElement} yearSelect Shortcut for native year select.
 * @property {HTMLInputElement} input Shortcut for input element.
 * @property {HTMLInputElement} control , Shortcut to control element.
 * @slot
 * @example
 * <ui-monthpicker>
 *   <input type="text" name="month">
 * </ui-monthpicker>
 */
@Component({ tag: 'ui-monthpicker', styles })
export class UIMonthPicker extends UIDateElement {
    /**
     * Layouts for ui-monthpicker.
     * @type {{REFINED: string}}
     */
    static readonly layouts: UIMonthPickerLayouts = {
        REFINED: 'refined',
    } as const;

    /**
     * @type {{INPUT: string, SELECT: string}}
     */
    static readonly modes: UIMonthPickerModes = {
        INPUT: 'input',
        SELECT: 'select',
    } as const;

    /**
     * Define labels what could be localised
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-monthpicker', {
            months: [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December',
            ],
            year: 'Year',
            month: 'Month',
            clearDate: 'Clear date',
        });
    }

    private static _defaultMinYear?: number;
    private static _defaultMaxYear?: number;

    static get defaultMinYear(): number {
        return UIMonthPicker._defaultMinYear || 1900;
    }

    static set defaultMinYear(value: number) {
        UIMonthPicker._defaultMinYear = value;
    }

    static get defaultMaxYear(): number {
        return UIMonthPicker._defaultMaxYear || 2100;
    }

    static set defaultMaxYear(value: number) {
        UIMonthPicker._defaultMaxYear = value;
    }

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['control-id', 'allow-empty'];
    }

    @Attribute(String) controlId?: string;
    @Attribute(String) layout?: string;
    @Attribute(Boolean) allowEmpty?: boolean;

    @Child('input') control: HTMLInputElement = null!;
    @Child('.ui-monthpicker__control.-month') monthSelect!: HTMLSelectElement;
    @Child('.ui-monthpicker__control.-year') yearSelect!: HTMLSelectElement;

    private _hasUserSetNativeId?: boolean;
    private observer?: MutationObserver;

    override get valueType(): string {
        return 'month';
    }

    get value(): NullOr<string> {
        const value = this.control.value;
        if (this.allowEmpty && !value) {
            return '';
        }
        return this.isValidMonth(value) ? value : null;
    }

    set value(value: NullOr<string>) {
        const isEmpty = value === null || value === '';
        if (isEmpty) {
            if (!this.allowEmpty) {
                return;
            }
            this.setControlValue(null);
            return;
        }
        if (!this.isValidMonth(value) || value === this.value) {
            return;
        }
        this.setControlValue(value);
    }

    get mode(): UIMonthPickerMode {
        return browser.isMobile()
            ? UIMonthPicker.modes.INPUT
            : UIMonthPicker.modes.SELECT;
    }

    get clearLink(): NullOr<Element> {
        return this.nextElementSibling?.classList.contains(
            'ui-monthpicker__clear-link'
        )
            ? this.nextElementSibling
            : null;
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'control-id':
                if (
                    this.mode === UIMonthPicker.modes.INPUT &&
                    this.control &&
                    !this._hasUserSetNativeId
                ) {
                    if (newValue) {
                        this.control.setAttribute('id', newValue);
                    } else {
                        this.control.removeAttribute('id');
                    }
                }
                break;
            case 'allow-empty':
                this.updateYearList();
                this.updateMonthList();
                this.updateClearLink();
                this.setControlValue(this.value);
                break;
        }
    }

    /**
     * Updates appearance according to current mode.
     * @returns {UIMonthPicker}
     */
    updateAppearance(): InstanceType<typeof UIMonthPicker> {
        if (this.mode === UIMonthPicker.modes.INPUT) {
            setAttributes(this.control, { type: 'month' });
            hide(this.monthSelect);
            hide(this.yearSelect);
        } else {
            setAttributes(this.control, { type: 'hidden' });
            show(this.monthSelect);
            show(this.yearSelect);
        }
        return this;
    }

    /**
     * Sets value to native controls.
     *
     * If allow-empty is set:
     * - `null` performs a full clear: the input is emptied AND both the month
     *   and year selects are reset to their empty option.
     * - `''` empties only the input and leaves the current month/year select
     *   choices untouched (used while the user is mid-edit).
     * @param {string} value
     * @returns {UIMonthPicker}
     */
    setControlValue(value: NullOr<string>): InstanceType<typeof UIMonthPicker> {
        if (!value) {
            if (this.allowEmpty) {
                if (
                    this.control &&
                    getNativeInputDescriptor().get?.call(this.control) !== ''
                ) {
                    setNativeInputValue(this.control, '');
                }
                if (value === null) {
                    this.monthSelect.value = '';
                    this.yearSelect.value = '';
                }
                const hasPartialSelection =
                    !!this.monthSelect?.value !== !!this.yearSelect?.value;
                hasPartialSelection ? this.addErrors() : this.clearErrors();
                this.updateClearLink();
                return this;
            }
            value = this.parseMonthValue('', this.min, this.max).value;
        }

        const data = this.parseMonthValue(value, this.min, this.max);
        const inputControl = this.control;

        if (
            inputControl &&
            getNativeInputDescriptor().get?.call(inputControl) !== data.value
        ) {
            setNativeInputValue(inputControl, data.value);
        }

        const { monthSelect, yearSelect } = this;

        if (monthSelect && parseInt(monthSelect.value) !== data.month) {
            this.updateMonthList();
            monthSelect.value = String(data.month);
        }

        if (yearSelect && parseInt(yearSelect.value) !== data.year) {
            yearSelect.value = String(data.year);
        }
        this.updateClearLink();
        return this;
    }

    /**
     * Returns min and max month values for select element based on given params
     * @param {string} value
     * @param {string} min
     * @param {string} max
     * @returns {Record<string, number>}
     */
    getVisibleMonths(
        value: NullOr<string>,
        min: NullOr<string>,
        max: NullOr<string>
    ): UIMonthPickerVisibleMonths {
        let monthMin = 1;
        let monthMax = 12;
        let currentYear = UIMonthPicker.defaultMinYear;

        if (this.isValidMonth(value)) {
            currentYear = this.parseMonthValue(value!, min, max).year;
        } else if (min) {
            currentYear = this.parseMonthValue(min, min, max).year;
        }

        if (min) {
            const minData = this.parseMonthValue(min, min, max);
            if (currentYear === minData.year) {
                monthMin = minData.month;
            }
        }

        if (max) {
            const maxData = this.parseMonthValue(max, min, max);
            if (currentYear === maxData.year) {
                monthMax = maxData.month;
            }
        }

        return {
            min: monthMin,
            max: monthMax,
        };
    }

    /**
     * Updates available options in the years select
     * @returns {UIMonthPicker}
     */
    updateYearList(): InstanceType<typeof UIMonthPicker> {
        const control = this.yearSelect;
        if (!control) {
            return this;
        }
        const value = control.value;
        detachChildNodes(control);
        const options: Array<ElementConfig> = [
            ...this.buildYearList().map(
                (year): ElementConfig => ({
                    tagName: 'option',
                    attributes: { value: year },
                    children: [String(year)],
                })
            ),
        ];
        if (this.allowEmpty) {
            options.unshift({
                tagName: 'option',
                attributes: { value: '' },
                children: [String(UIMonthPicker.labels.year)],
            });
        }
        insertElements(control, options);
        control.value = value;
        return this;
    }

    /**
     * Get available years
     * @returns {Array<number>}
     */
    buildYearList(): Array<number> {
        const years: Array<number> = [];
        let yearMin = UIMonthPicker.defaultMinYear;
        let yearMax = UIMonthPicker.defaultMaxYear;

        if (this.min) {
            yearMin = parseInt(this.min.split('-')[0]);
        }
        if (this.max) {
            yearMax = parseInt(this.max.split('-')[0]);
        }

        yearMin = Math.min(yearMin, yearMax);
        yearMax = Math.max(yearMin, yearMax);

        for (let i = yearMin - 1; i < yearMax; i++) {
            years.push(i + 1);
        }

        return years;
    }

    /**
     * Updates available options in the months select
     * @returns {UIMonthPicker}
     */
    updateMonthList(): InstanceType<typeof UIMonthPicker> {
        const control = this.monthSelect;
        if (!control) {
            return this;
        }
        const value = control.value;
        detachChildNodes(control);
        const options: Array<ElementConfig> = [
            ...this.buildMonthList().map((month): ElementConfig => {
                const monthLabels = UIMonthPicker.labels.months;
                const monthLabel = Array.isArray(monthLabels)
                    ? String(monthLabels[month - 1] ?? '')
                    : '';
                return {
                    tagName: 'option',
                    attributes: { value: month },
                    children: [monthLabel],
                };
            }),
        ];
        if (this.allowEmpty) {
            options.unshift({
                tagName: 'option',
                attributes: { value: '' },
                children: [String(UIMonthPicker.labels.month)],
            });
        }
        insertElements(control, options);
        control.value = value;
        return this;
    }

    /**
     * Get available months
     * @returns {Array<number>}
     */
    buildMonthList(): Array<number> {
        const months: Array<number> = [];
        const data = this.parseMonthValue(this.value!, this.min, this.max);
        const visibleRange = this.getVisibleMonths(
            data.value,
            this.min,
            this.max
        );
        // Cannot use Array.from() because of legacy ibank
        for (let i = visibleRange.min - 1; i < visibleRange.max; i++) {
            months.push(i + 1);
        }

        return months;
    }

    /**
     * Fires callback when the host's value is updated
     * @private
     */
    private handleValueUpdate = (): void => {
        this.setControlValue(this.value);
    };

    /**
     * Fires callback when the month/year selects value is updated
     * @param {Event} e
     * @private
     */
    private handleMonthUpdate = (e?: Event | CustomEvent): void => {
        e?.stopPropagation();
        const monthVal = this.monthSelect.value;
        const yearVal = this.yearSelect.value;
        this.clearErrors();

        const hasEmptyField = !monthVal || !yearVal;
        const hasPartialSelection = Boolean(monthVal) !== Boolean(yearVal);

        if (this.allowEmpty && hasEmptyField) {
            if (hasPartialSelection) {
                this.addErrors();
            }
            setNativeInputValue(this.control, '');
            dispatchNativeEvent(this, 'change', true);
            dispatchNativeEvent(this.control, 'input', true);
            return;
        }

        const month = Number(monthVal);
        const year = Number(yearVal);
        this.setControlValue(`${year}-${month}`);
        dispatchNativeEvent(this, 'change', true);
        dispatchNativeEvent(this.control, 'input', true);
        if (this.layout === UIMonthPicker.layouts.REFINED) {
            this.recalculateMonthWidth();
        }
        this.updateMonthList();
    };

    /**
     * Fires callback when the input's value is updated
     * @private
     */
    private handleInputChange = (): void => {
        if (!this.control.value && this.allowEmpty) {
            setNativeInputValue(this.control, '');
            this.dispatchNativeEvent('change');
        } else {
            this.setControlValue(this.value).dispatchNativeEvent('change');
        }
        dispatchNativeEvent(this.control, 'input', true);
    };

    /**
     * Fires callback when browser params are updated
     * @private
     */
    private handleBrowserUpdate = (): void => {
        this.updateAppearance().setControlValue(this.value);
    };

    /**
     * Fires callback when any of control gets a blur event
     * @private
     */
    private handleControlBlur = (): void => {
        dispatchNativeEvent(this, 'blur');
    };

    /**
     * @inheritDoc
     */
    handleControlMutations(mutations: Array<MutationRecord>): void {
        for (const mutation of mutations) {
            if (mutation.type !== 'attributes') {
                continue;
            }
            if (['value', 'min', 'max'].includes(mutation.attributeName!)) {
                if (
                    mutation.oldValue !==
                    (mutation.target as Element).getAttribute(
                        mutation.attributeName!
                    )
                ) {
                    this.updateYearList();
                    this.updateMonthList();
                    this.setControlValue(this.value);
                    return;
                }
            }
        }
    }

    /**
     * Convert to date string based on locale.
     * @param {string} str
     * @returns {string}
     */
    formatMonthValue(str: string): string {
        if (!str) {
            return '';
        }
        const value = this.parseDateString(str);
        return [value.year, addLeadingZero(value.month)].join('-');
    }

    private handlePageShow = (): void => {
        const month = Number(this.monthSelect.value);
        const year = Number(this.yearSelect.value);
        if (year && month) {
            this.setControlValue(year + '-' + month);
        }
    };

    /**
     * Recalculate element's width. Should not be called manually.
     */
    recalculateMonthWidth(): void {
        const helper = this.querySelector<HTMLElement>(
            '.ui-monthpicker__month-w-helper'
        );
        if (!helper) {
            return;
        }
        const compStyles = browser.run<CSSStyleDeclaration | null>(
            () => window.getComputedStyle(this.monthSelect),
            null
        );
        if (!compStyles) {
            return;
        }
        const pr = parseInt(compStyles.getPropertyValue('padding-right'));
        const pl = parseInt(compStyles.getPropertyValue('padding-left'));
        const opt =
            this.monthSelect.querySelector<HTMLOptionElement>('option:checked');
        helper.innerText = opt?.innerText ?? '';
        const w = helper.offsetWidth + pr + pl;
        this.style.setProperty('--month-width', `${w}px`);
        helper.innerText = '';
    }

    /**
     * Returns the control ID that should be used by ui-field for label association.
     * SELECT mode: control-id if provided, otherwise hidden input id (get/set if missing).
     * INPUT mode: user id on input > control-id > generated (same as ui-datepicker); always returns a string.
     * @returns {string}
     */
    getControlIdForField(): string {
        if (this.mode === UIMonthPicker.modes.SELECT) {
            const controlIdAttr = this.getAttribute('control-id');
            if (controlIdAttr) {
                return controlIdAttr;
            }
            const hiddenInput = this.querySelector<HTMLInputElement>(
                'input[type="hidden"].ui-monthpicker__input'
            );
            if (hiddenInput) {
                let inputId = hiddenInput.getAttribute('id') || hiddenInput.id;
                if (!inputId) {
                    inputId = Digest.randomId();
                    hiddenInput.id = inputId;
                    hiddenInput.setAttribute('id', inputId);
                }
                return inputId;
            }
            return Digest.randomId();
        }

        // INPUT mode: user id > control-id > generated
        const controlIdAttr = this.controlId || this.getAttribute('control-id');
        const input =
            this.control ||
            (this.queryChildren('input')[0] as HTMLInputElement);
        if (input) {
            const inputId = input.getAttribute('id')?.trim();
            if (inputId) {
                return inputId;
            }
            if (controlIdAttr) {
                if (input.id !== controlIdAttr) {
                    input.id = controlIdAttr;
                    input.setAttribute('id', controlIdAttr);
                }
                return controlIdAttr;
            }
            const effectiveId = input.id || Digest.randomId();
            if (input.id !== effectiveId) {
                input.id = effectiveId;
                input.setAttribute('id', effectiveId);
            }
            return effectiveId;
        }
        return controlIdAttr || Digest.randomId();
    }

    /**
     * Sets a custom validation error on the native inputs.
     * @private
     */
    private addErrors(): void {
        if (!this.monthSelect || !this.yearSelect) {
            return;
        }

        this.monthSelect.setCustomValidity(String(validationLabels.matches));
        this.yearSelect.setCustomValidity(String(validationLabels.matches));

        this.monthSelect.checkValidity();
        this.yearSelect.checkValidity();
    }

    /**
     * Clears any previously set custom validation error from native inputs.
     * @private
     */
    private clearErrors(): void {
        if (!this.monthSelect || !this.yearSelect) {
            return;
        }
        if (
            !this.monthSelect.validationMessage &&
            !this.yearSelect.validationMessage
        ) {
            return;
        }

        this.monthSelect.setCustomValidity('');
        this.yearSelect.setCustomValidity('');

        this.monthSelect.dispatchEvent(new Event('input', { bubbles: true }));
        this.yearSelect.dispatchEvent(new Event('input', { bubbles: true }));
    }

    /**
     * @inheritDoc
     */
    private getControlElement(): HTMLInputElement {
        return this.control;
    }

    /**
     * @inheritDoc
     */
    private onValuePatched(newValue: AttributeValue): void {
        const value = newValue as NullOr<string>;
        if (!value && !this.allowEmpty) {
            return;
        }
        this.setControlValue(value);
        dispatchNativeEvent(this, 'change', true);
    }

    /**
     * Builds clear date link
     * @returns {ElementConfig}
     * @private
     */
    private buildClearLink(): HTMLElement {
        return this.createElement({
            tagName: 'ui-link',
            classList: {
                'ui-monthpicker__clear-link': true,
            },
            attributes: {
                'layout': 'basic',
                'slot': 'trailing',
                'label': UIMonthPicker.labels.clearDate,
                'is-button': true,
            },
        });
    }

    /**
     * Handles click on the clear date link.
     * @private
     */
    private handleClearLinkClick = (e: Event): void => {
        if (!this.allowEmpty) {
            return;
        }
        e.preventDefault(); // prevents scrolling
        this.setControlValue(null);
        dispatchNativeEvent(this, 'change', true);
        dispatchNativeEvent(this.control, 'input', true);
    };

    /**
     * Whether both the month and year selects are empty.
     * @private
     */
    private isSelectionEmpty(): boolean {
        return !this.monthSelect?.value && !this.yearSelect?.value;
    }

    /**
     * Adds or removes the clear date link in SELECT mode.
     * The link is only shown when empty values are allowed and there is a
     * value to clear, i.e. at least one of the month/year selects is set.
     * @private
     */
    private updateClearLink(): void {
        if (!this.rendered || this.mode !== UIMonthPicker.modes.SELECT) {
            return;
        }
        const existingLink = this.nextElementSibling?.classList.contains(
            'ui-monthpicker__clear-link'
        )
            ? this.nextElementSibling
            : null;
        const shouldShowLink = this.allowEmpty && !this.isSelectionEmpty();
        if (shouldShowLink && !existingLink) {
            const link = this.buildClearLink();
            this.insertAdjacentElement('afterend', link);
            link.addEventListener('click', this.handleClearLinkClick);
        } else if (!shouldShowLink && existingLink) {
            existingLink.remove();
        }
    }

    /**
     * @inheritDoc
     */
    private onDisabledStateChanged(newState: boolean): void {
        this.monthSelect.disabled = newState;
        this.yearSelect.disabled = newState;
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const control = this.queryChildren(
            'input'
        )[0] as UndefinedOr<HTMLInputElement>;
        if (control) {
            if (control.min) {
                control.setAttribute('min', this.formatMonthValue(control.min));
            }
            if (control.max) {
                control.setAttribute('max', this.formatMonthValue(control.max));
            }
            const valueStr = control.getAttribute('value');
            if (valueStr) {
                control.setAttribute('value', this.formatMonthValue(valueStr));
            }
            if (this.getAttribute('value')) {
                control.setAttribute('value', this.getAttribute('value')!);
            }
        }

        const isInsideField = !!this.closest('ui-field');
        let inputId: NullOr<string> = control?.getAttribute('id') || null;
        let baseId: string;

        if (this.mode === UIMonthPicker.modes.INPUT) {
            // INPUT mode: user id on input > control-id > generated
            if (this._hasUserSetNativeId === undefined) {
                this._hasUserSetNativeId = !!inputId?.trim();
            }
            if (!inputId && !isInsideField) {
                inputId = Digest.randomId();
                if (control) {
                    control.setAttribute('id', inputId);
                }
            }
            baseId =
                inputId && typeof inputId === 'string'
                    ? inputId
                    : Digest.randomId();
        } else {
            // SELECT mode: use control-id if user provides it, otherwise derive from input id or generate
            // The base id is set on the hidden input so ui-field.getControlId() can find it
            const controlIdAttr = this.controlId; // Only use if user explicitly provides it
            if (controlIdAttr) {
                baseId = controlIdAttr;
            } else if (inputId) {
                baseId = inputId;
            } else {
                baseId = Digest.randomId();
            }
        }

        let elements: Array<ElementConfig> = [
            {
                tagName: 'input',
                element: control || null,
                classList: {
                    'ui-monthpicker__input': true,
                },
                attributes: {
                    pattern: '[0-9]{4}-[0-9]{2}',
                    // INPUT mode: input gets the id directly
                    // SELECT mode: input is hidden but gets baseId so ui-field.getControlId() can find it
                    id: baseId,
                },
            },
        ];
        let monthControl: ElementConfig = {
            tagName: 'select',
            attributes: {
                'id': `${baseId}-month`,
                'data-defer-validation': true,
            },
            classList: {
                'ui-monthpicker__control': true,
                '-month': true,
                '-refined': this.layout === UIMonthPicker.layouts.REFINED,
            },
        };
        let yearControl: ElementConfig = {
            tagName: 'select',
            attributes: {
                'id': `${baseId}-year`,
                'data-defer-validation': true,
            },
            classList: {
                'ui-monthpicker__control': true,
                '-year': true,
                '-refined': this.layout === UIMonthPicker.layouts.REFINED,
            },
        };

        if (this.layout === UIMonthPicker.layouts.REFINED) {
            yearControl = {
                tagName: 'div',
                classList: {
                    'ui-monthpicker__control-container': true,
                    '-year': true,
                    '-hidden': this.mode === UIMonthPicker.modes.INPUT,
                },
                children: [yearControl],
            };
        }

        if (this.layout === UIMonthPicker.layouts.REFINED) {
            monthControl = {
                tagName: 'div',
                classList: {
                    'ui-monthpicker__control-container': true,
                    '-month': true,
                    '-hidden': this.mode === UIMonthPicker.modes.INPUT,
                },
                children: [
                    monthControl,
                    {
                        tagName: 'div',
                        attributes: {
                            'aria-hidden': true,
                            'aria-live': 'none',
                        },
                        classList: {
                            'ui-monthpicker__month-w-helper': true,
                        },
                    },
                ],
            };
        }

        const accessibilityLabels = ['year', 'month'].map(
            (t): ElementConfig => {
                return {
                    tagName: 'label',
                    attributes: {
                        for: `${baseId}-${t}`,
                    },
                    classList: {
                        '-visually-hidden': true,
                    },
                    children: [UIMonthPicker.labels[t]],
                };
            }
        );

        elements = elements.concat(
            !UIMonthPicker.LithuanianLocales.includes(this.locale)
                ? [monthControl, yearControl]
                : [yearControl, monthControl],
            accessibilityLabels
        );

        this.insertElements(elements);
        this.updateYearList();
        this.updateMonthList();

        this.updateAppearance().setControlValue(control ? control.value : null);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        eventBus.addEventListener('browser:update', this.handleBrowserUpdate);

        this.updateAppearance().setControlValue(this.value);
        this.updateClearLink();
        this.clearLink?.addEventListener('click', this.handleClearLinkClick);

        this.updateElement({
            events: {
                change: this.handleValueUpdate,
            },
        });

        updateElement(this.control, {
            events: {
                change: this.handleInputChange,
                blur: this.handleControlBlur,
            },
        });

        updateElement(this.monthSelect, {
            events: {
                change: this.handleMonthUpdate,
                blur: this.handleControlBlur,
            },
        });

        updateElement(this.yearSelect, {
            events: {
                change: this.handleMonthUpdate,
                blur: this.handleControlBlur,
            },
        });

        this.observer = new MutationObserver(
            this.handleControlMutations.bind(this)
        );
        this.observer.observe(this.control, { attributes: true });

        window.addEventListener('pageshow', this.handlePageShow);
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        eventBus.removeEventListener(
            'browser:update',
            this.handleBrowserUpdate
        );
        window.removeEventListener('pageshow', this.handlePageShow);
        this.clearLink?.removeEventListener('click', this.handleClearLinkClick);
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        eventBus.addEventListener('browser:update', this.handleBrowserUpdate);
        window.addEventListener('pageshow', this.handlePageShow);
        this.updateClearLink();
        this.clearLink?.addEventListener('click', this.handleClearLinkClick);
    }
}
